import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Suchbare Lieferanten-Combobox – optimiert für 100–3000+ Einträge.
 * value = Liefernummer (String) oder null
 * onChange(liefernummer, supplierObj)
 * options = optional vorgegebene Liste (Default: /api/lieferanten)
 * filter = optionale Vorfilter-Function
 */
export const SupplierPicker = ({
  value,
  onChange,
  placeholder = "Lieferant wählen …",
  testId = "supplier-picker",
  disabled = false,
  options = null,
  filter = null,
  allowClear = true,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(options || []);
  const [loading, setLoading] = useState(!options);
  const [suche, setSuche] = useState("");

  useEffect(() => {
    if (options) { setRows(options); return; }
    setLoading(true);
    api.get("/lieferanten")
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [options]);

  const gefiltert = useMemo(() => {
    let list = rows;
    if (filter) list = list.filter(filter);
    if (suche.trim()) {
      const q = suche.trim().toLowerCase();
      list = list.filter((l) =>
        (l.liefernummer || "").includes(q) ||
        (l.name || "").toLowerCase().includes(q) ||
        (l.hofname || "").toLowerCase().includes(q) ||
        (l.ort || "").toLowerCase().includes(q)
      );
    }
    return list.slice(0, 200); // Performance-Cap
  }, [rows, suche, filter]);

  const selected = value ? rows.find((r) => r.liefernummer === value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testId}
          className={cn("w-full justify-between font-normal", className)}
        >
          {selected ? (
            <span className="truncate">
              <span className="font-medium font-num">{selected.liefernummer}</span>
              <span className="text-muted-foreground"> · {selected.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{loading ? "Lade …" : placeholder}</span>
          )}
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {allowClear && selected && (
              <X className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); onChange(null, null); }} />
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }} align="start">
        <Command shouldFilter={false} data-testid={`${testId}-command`}>
          <CommandInput
            placeholder="Suche Liefernummer, Name, Hof oder Ort …"
            value={suche}
            onValueChange={setSuche}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            {loading ? (
              <div className="p-4 text-sm text-muted-foreground">Lade Lieferanten …</div>
            ) : gefiltert.length === 0 ? (
              <CommandEmpty>Keine Lieferanten gefunden.</CommandEmpty>
            ) : (
              <CommandGroup>
                {gefiltert.map((l) => (
                  <CommandItem
                    key={l.liefernummer}
                    value={l.liefernummer}
                    data-testid={`${testId}-option-${l.liefernummer}`}
                    onSelect={() => {
                      onChange(l.liefernummer, l);
                      setOpen(false);
                      setSuche("");
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4",
                      selected?.liefernummer === l.liefernummer ? "opacity-100" : "opacity-0")} />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <span className="font-num font-medium">{l.liefernummer}</span>
                        <span className="text-xs text-muted-foreground truncate">{l.milchsorte} · {l.tour}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {l.name} · {l.hofname} · {l.ort}
                      </div>
                    </div>
                  </CommandItem>
                ))}
                {rows.length > gefiltert.length + 200 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground border-t">
                    … {rows.length - gefiltert.length} weitere. Bitte präzisieren Sie die Suche.
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
